// GA4 consent bridge.
//
// The bootstrap (window.dataLayer + window.gtag stub + Consent Mode v2
// default + `config` command + gtag.js script load) lives inline in
// index.html, which runs synchronously before the React bundle. By the
// time this module loads, dataLayer is already populated and gtag.js
// is already on its way down from googletagmanager.com.
//
// This module's only job is the React-driven side: when the
// ConsentProvider's state changes, push a `consent update` so gtag
// knows whether to actually send the queued hits. The earlier version
// also handled gtag stub + script injection from here, but that
// triggered a useEffect ordering race — see PRs #608/#609/#610 and the
// inline comment in index.html for the full post-mortem.
//
// Driven by ConsentContext via a useEffect that watches all three
// optional axes. The caller never has to inspect window globals or
// split between "load" and "revoke" paths — the function reflects
// whatever state it's given.

import { isNativeApp } from '../platform';

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Cached at module load — the env var can't change between calls.
const IS_CONFIGURED = Boolean(MEASUREMENT_ID && MEASUREMENT_ID.trim().length > 0);
let warnedMissingId = false;
export function isAnalyticsConfigured() {
  if (!IS_CONFIGURED && !warnedMissingId) {
    console.warn('[GA4] VITE_GA_MEASUREMENT_ID is not set — Google Analytics will not load. Set it in your .env / Vercel env to enable analytics.');
    warnedMissingId = true;
  }
  return IS_CONFIGURED;
}

// Clear the first-party GA cookies. Idempotent — calling expire on a
// cookie that was never set is a no-op the browser silently absorbs,
// so it's safe to invoke whenever analytics is off without checking
// whether cookies actually exist.
//
// Cookie domain coverage:
//   - Default path=/ (host-only) and `domain=.<host>` cover the basic
//     case where gtag set the cookie on the page's exact host or its
//     leading-dot variant.
//   - Apex (last two parts) covers subdomain deployments — e.g. on
//     www.petzy.ro, GA sets cookies on .petzy.ro so they're shared
//     with the apex host. We only attempt this when the host has 3+
//     parts (a 2-part host like petzy.ro IS the apex) AND we're not
//     on a Vercel preview domain (where .vercel.app is a public
//     suffix the browser would silently block — harmless but noisy).
//   - Multi-part TLDs (.co.uk, .com.ro) would mis-compute the apex
//     here (parts.slice(-2) yields the public-suffix portion). The
//     attempt is silently rejected by the browser, so the failure
//     mode is a missed clear, not a crash. Acceptable for current
//     scale; revisit when we deploy on a multi-part TLD.
function clearGaCookies() {
  if (!MEASUREMENT_ID || typeof window === 'undefined') return;
  const host = window.location.hostname;
  const parts = host.split('.');
  const apex = parts.length > 2 ? parts.slice(-2).join('.') : null;
  const expire = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`;
    if (apex && !host.endsWith('.vercel.app')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${apex}`;
    }
  };
  // _ga is the cross-property visitor id; _ga_<MEASUREMENT_ID> is the
  // session counter. Both are first-party and within scope of our
  // disclosure.
  expire('_ga');
  expire(`_ga_${MEASUREMENT_ID.replace(/^G-/, '')}`);
}

// Inject the gtag.js script tag if it's not already in the DOM.
// Idempotent — bails the moment a tag matching the measurement-id
// query already exists. Used by the dynamic-opt-in path: a returning
// visitor who originally clicked "Refuz total" hits the strict-refuse
// branch in index.html, which skips the script tag entirely; if they
// later flip Analize on via the settings modal, syncConsentToGtag
// calls this so the consent update they're about to push actually
// has a script to receive it.
function ensureGtagScriptInjected() {
  // Native app loads no web analytics trackers (App Store 5.1.2 / ATT).
  if (isNativeApp) return;
  if (!MEASUREMENT_ID || typeof document === 'undefined') return;
  // Selector pinned to our specific measurement id (not just any
  // googletagmanager script) so if a future tag — Google Ads, a
  // co-branded property, a third-party widget — happens to drop its
  // own gtag/js into the page, we still detect that OUR script is
  // missing and inject it. The encodeURIComponent matches the same
  // encoding the inline bootstrap in index.html uses, so the
  // attribute compare is exact.
  const existing = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}"]`,
  );
  if (existing) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(s);
}

// Push the current consent state into gtag. Maps the four Consent
// Mode v2 axes onto our three optional consent categories:
//
//   analytics_storage   ← analytics       (GA4 measurement)
//   ad_storage,
//   ad_user_data        ← marketing       (Google Ads / DV360 storage
//                                          and user-data signals)
//   ad_personalization  ← personalization (personalized ads/content)
//
// The gtag stub is bootstrapped from index.html (defined
// unconditionally, even on the strict-refuse path), so `window.gtag`
// is always a function by the time this runs. The actual gtag.js
// script may or may not be on the page — strict-refuse skips it —
// and we inject it on demand below if the user just flipped analytics
// to granted. GA cookies are cleared on every transition where
// analytics is off, which idempotently covers grant→revoke and the
// first-paint no-consent state.
export function syncConsentToGtag({ analytics, marketing, personalization }) {
  if (!isAnalyticsConfigured()) return;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  window.gtag('consent', 'update', {
    analytics_storage:    analytics       ? 'granted' : 'denied',
    ad_storage:           marketing       ? 'granted' : 'denied',
    ad_user_data:         marketing       ? 'granted' : 'denied',
    ad_personalization:   personalization ? 'granted' : 'denied',
  });

  // Dynamic opt-in: visitors who originally refused didn't get the
  // script tag from the inline bootstrap. The moment they grant
  // analytics through the settings modal, inject it so the consent
  // update we just pushed (and the next page_view from
  // useGA4PageView) actually reach Google.
  if (analytics) {
    ensureGtagScriptInjected();
  }

  if (!analytics) {
    clearGaCookies();
  }
}
